import SessionCommentPanel from "./SessionCommentPanel";
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import CreateNodeSection from '../CreateNodeSection';
import GraphBootstrapFromCode from '../GraphFromCode';
import TextToNodeCreator from '../TextToNodeCreator';
import PendingNodesPanel from '../PendingNodesPanel';
import TestCoverageTable from '../TestCoverageTable';
import GlobalTestsGraph from '../GlobalTestsGraph';
import DocumentAnalyzer from './DocumentAnalyzer';
import Button from '../ui/button';
import Spinner from '../ui/spinner';
import { FaCheckCircle, FaPlay, FaLock, FaClock } from 'react-icons/fa';
import './NodementedFunnel.css';
// 🔌 NOUVEAU - Intégrations JIRA/GitHub/GitLab
import { useIntegrations } from '../../hooks/useIntegrations';
import IntegrationPlaceholder from '../integrations/IntegrationPlaceholder';
import JiraAnalyzerModal from '../integrations/JiraAnalyzerModal';
import IntegrationSetupModal from '../integrations/IntegrationSetupModal';

/**
 * Funnel d'usage Nodemented - Composant principal qui guide l'utilisateur
 * dans le workflow complet : Ingestion → Revue → Validation → Export
 */
import { useEffect } from 'react';
const NDMNTD_STEP_KEY = 'nodemented_funnel_step';
const NodementedFunnel = ({
  user,
  nodes = [],
  onFunnelComplete,
  initialStep = 'ingestion',
  addPendingNode,
  pendingNodes,
  handleValidateNode,
  handleEditNode,
  handleDeleteNode,
  savePendingNodeEdit,
  editingPendingNode,
  cancelPendingNodeEdit,
  setNodes,
  loading,
  setLoading,
  nodeName,
  setNodeName,
  nodeType,
  setNodeType,
  technology,
  setTechnology,
  status,
  setStatus,
  nodeDescription,
  setNodeDescription,
  metadata,
  setMetadata,
  endpointSchema,
  setEndpointSchema,
  saveNode,
  updateNodeSimple,
  resetForm,
  previewNode,
  setPreviewNode,
  showPreview,
  setShowPreview,
  previewError,
  setPreviewError
}) => {
  const { t } = useTranslation();
  // Restaure l'étape depuis le localStorage si présente
  const getInitialStep = () => {
    const cached = localStorage.getItem(NDMNTD_STEP_KEY);
    return cached || initialStep;
  };
  const [currentStep, setCurrentStep] = useState(getInitialStep());
  const [stepCompleted, setStepCompleted] = useState({});
  const [stepData, setStepData] = useState({});
  const [reviewMode, setReviewMode] = useState('solo'); // 'solo' ou 'team'
  const [exportFormats, setExportFormats] = useState([]);
  const [expandedMethod, setExpandedMethod] = useState(null); // Pour l'accordéon des méthodes
  const [showPendingAlert, setShowPendingAlert] = useState(false);
  
  // 🔌 NOUVEAU - États pour les intégrations JIRA/GitHub
  const { integrations, refreshIntegrations } = useIntegrations();
  const [showIntegrationSetup, setShowIntegrationSetup] = useState(false);
  const [showJiraAnalyzer, setShowJiraAnalyzer] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null); // 'atlassian', 'github', 'gitlab'
  
  // Étapes du funnel avec leur configuration détaillée
  const FUNNEL_STEPS = [
    { 
      id: 'ingestion', 
      label: t('nodementedFunnel.steps.ingestion.label'),
      icon: '📥',
      description: t('nodementedFunnel.steps.ingestion.description'),
      color: '#10b981'
    },
    { 
      id: 'review', 
      label: t('nodementedFunnel.steps.review.label'),
      icon: '👥',
      description: t('nodementedFunnel.steps.review.description'),
      color: '#3b82f6'
    },
    { 
      id: 'validation', 
      label: t('nodementedFunnel.steps.validation.label'),
      icon: '✅',
      description: t('nodementedFunnel.steps.validation.description'),
      color: '#8b5cf6'
    },
    { 
      id: 'export', 
      label: t('nodementedFunnel.steps.export.label'),
      icon: '📊',
      description: t('nodementedFunnel.steps.export.description'),
      color: '#f59e0b'
    }
  ];

  // Persiste l'étape courante à chaque changement
  useEffect(() => {
    if (currentStep) {
      localStorage.setItem(NDMNTD_STEP_KEY, currentStep);
    }
  }, [currentStep]);

  const goToStep = (stepId) => {
    // Vérifier si l'étape est accessible
    const stepIndex = FUNNEL_STEPS.findIndex(s => s.id === stepId);
    const currentIndex = FUNNEL_STEPS.findIndex(s => s.id === currentStep);
    // Permettre de revenir en arrière ou d'aller à l'étape suivante si complétée
    if (stepIndex <= currentIndex || stepCompleted[FUNNEL_STEPS[stepIndex - 1]?.id]) {
      setCurrentStep(stepId);
    }
  };
  
  const completeStep = (stepId, data = {}) => {
    setStepCompleted(prev => ({ ...prev, [stepId]: true }));
    setStepData(prev => ({ ...prev, [stepId]: data }));
  };

  const getStepStatus = (stepId) => {
    const stepIndex = FUNNEL_STEPS.findIndex(s => s.id === stepId);
    const currentIndex = FUNNEL_STEPS.findIndex(s => s.id === currentStep);
    
    if (stepCompleted[stepId]) return 'completed';
    if (stepId === currentStep) return 'active';
    if (stepIndex <= currentIndex || (stepIndex > 0 && stepCompleted[FUNNEL_STEPS[stepIndex - 1]?.id])) return 'accessible';
    return 'locked';
  };

  const toggleMethod = (methodId) => {
    setExpandedMethod(expandedMethod === methodId ? null : methodId);
  };

  // Composant Accordéon pour les méthodes d'ingestion
  const IngestionMethodAccordion = ({ icon, title, subtitle, description, features, isExpanded, onToggle, content }) => {
    return (
      <div className={`method-accordion ${isExpanded ? 'expanded' : ''}`}>
        <div className="method-header" onClick={onToggle}>
          <div className="method-header-left">
            <span className="method-icon">{icon}</span>
            <div>
              <h4 className="method-title">{title}</h4>
              <p className="method-subtitle">{subtitle}</p>
            </div>
          </div>
          <div className="method-expand-icon">
            {isExpanded ? '▲' : '▼'}
          </div>
        </div>
        <div className="method-content">
          <div className="method-content-inner">
            <p className="method-description">{description}</p>
            <div className="method-features">
              {features.map((feature, index) => (
                <div key={index} className="feature-item">
                  <span className="feature-icon">{feature.icon}</span>
                  <span className="feature-text">{feature.text}</span>
                </div>
              ))}
            </div>
            {content}
          </div>
        </div>
      </div>
    );
  };

  let stepContent = null;
  if (currentStep === 'ingestion') {
    stepContent = (
      <div className="funnel-step-content ingestion-step">
        <div className="step-header">
          <div className="step-icon">📥</div>
          <div className="step-info">
            <h3>{t('nodementedFunnel.ingestion.title')}</h3>
            <p>{t('nodementedFunnel.ingestion.subtitle')}</p>
          </div>
        </div>

        <div className="ingestion-methods">

          <IngestionMethodAccordion
            icon="💬"
            title={t('nodementedFunnel.methods.description.title')}
            subtitle={t('nodementedFunnel.methods.description.subtitle')}
            description={t('nodementedFunnel.methods.description.description')}
            features={[
              { icon: "✓", text: t('nodementedFunnel.methods.description.features.ai') },
              { icon: "✓", text: t('nodementedFunnel.methods.description.features.natural') },
              { icon: "✓", text: t('nodementedFunnel.methods.description.features.fast') },
              { icon: "✓", text: t('nodementedFunnel.methods.description.features.smart') }
            ]}
            isExpanded={expandedMethod === 'description'}
            onToggle={() => toggleMethod('description')}
            content={<TextToNodeCreator addPendingNode={addPendingNode} />}
          />

          <IngestionMethodAccordion
            icon="📁"
            title={t('nodementedFunnel.methods.code.title')}
            subtitle={t('nodementedFunnel.methods.code.subtitle')}
            description={t('nodementedFunnel.methods.code.description')}
            features={[
              { icon: "✓", text: t('nodementedFunnel.methods.code.features.multi') },
              { icon: "✓", text: t('nodementedFunnel.methods.code.features.static') },
              { icon: "✓", text: t('nodementedFunnel.methods.code.features.detection') },
              { icon: "✓", text: t('nodementedFunnel.methods.code.features.relations') }
            ]}
            isExpanded={expandedMethod === 'code'}
            onToggle={() => toggleMethod('code')}
            content={<GraphBootstrapFromCode onGraphValidated={setNodes} />}
          />

          {/* 🔌 NOUVEAU - Intégration JIRA/GitHub/GitLab */}
          <IngestionMethodAccordion
            icon="🔷"
            title={t('nodementedFunnel.jiraIntegration.title')}
            subtitle={t('nodementedFunnel.jiraIntegration.subtitle')}
            description={t('nodementedFunnel.jiraIntegration.description')}
            features={[
              { icon: "✓", text: t('nodementedFunnel.jiraIntegration.features.llmAnalysis') },
              { icon: "✓", text: t('nodementedFunnel.jiraIntegration.features.acceptanceCriteria') },
              { icon: "✓", text: t('nodementedFunnel.jiraIntegration.features.gherkinTests') },
              { icon: "✓", text: t('nodementedFunnel.jiraIntegration.features.security') }
            ]}
            isExpanded={expandedMethod === 'jira'}
            onToggle={() => toggleMethod('jira')}
            content={
              integrations?.atlassian?.connected ? (
                <div style={{ padding: '20px', textAlign: 'center' }}>
                  <p style={{ marginBottom: '16px', color: '#555' }}>
                    {t('nodementedFunnel.jiraIntegration.configured')} <strong>{integrations.atlassian.domain}</strong>
                  </p>
                  <Button 
                    onClick={() => setShowJiraAnalyzer(true)}
                    className="integration-button integration-button-primary"
                    style={{
                      background: '#2196f3',
                      color: 'white',
                      padding: '12px 24px',
                      borderRadius: '8px',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '15px',
                      fontWeight: '600'
                    }}
                  >
                    {t('nodementedFunnel.jiraIntegration.analyzeTicket')}
                  </Button>
                </div>
              ) : (
                <IntegrationPlaceholder 
                  provider="atlassian"
                  onConfigure={() => {
                    setSelectedProvider('atlassian');
                    setShowIntegrationSetup(true);
                  }}
                />
              )
            }
          />

          {/* TODO: Réintégrer une section 'Analyse de Documents' dédiée si besoin plus tard */}
        </div>

        <div className="step-actions">
          <Button 
            onClick={() => {
              // Vérification : au moins un nœud créé doit être dans les "en cours"
              if (!pendingNodes || pendingNodes.length === 0) {
                setShowPendingAlert(true);
                return;
              }
              completeStep('ingestion', { 
                pendingNodesCount: pendingNodes?.length || 0,
                method: 'multiple' 
              }); 
              goToStep('review'); 
            }}
            className="next-step-btn"
            disabled={false}
          >
            <span>Étape suivante : Revue</span>
            <span className="step-arrow">→</span>
          </Button>
          {showPendingAlert && (
            <div style={{
              background: 'rgba(255,255,255,0.98)',
              border: '2px solid #f43f5e',
              borderRadius: '18px',
              boxShadow: '0 8px 32px rgba(244,63,94,0.08)',
              color: '#be123c',
              padding: '28px 32px',
              marginTop: '24px',
              textAlign: 'center',
              fontWeight: 700,
              fontSize: '1.15rem',
              zIndex: 1000
            }}>
              <div style={{fontSize: '2.2rem', marginBottom: 12}}>⚠️</div>
              <div dangerouslySetInnerHTML={{__html: t('nodementedFunnel.continueWorkflow')}}></div>
              <button
                style={{
                  marginTop: 18,
                  background: 'linear-gradient(90deg,#f43f5e,#be123c)',
                  color: 'white',
                  border: 'none',
                  borderRadius: 12,
                  padding: '10px 22px',
                  fontWeight: 700,
                  fontSize: '1rem',
                  cursor: 'pointer',
                  boxShadow: '0 2px 8px rgba(244,63,94,0.13)'
                }}
                onClick={() => setShowPendingAlert(false)}
              >OK</button>
            </div>
          )}
        </div>
      </div>
    );
  } else if (currentStep === 'review') {
    stepContent = (
      <div className="funnel-step-content review-step">
        <div className="step-header">
          <div className="step-icon">👥</div>
          <div className="step-info">
            <h3>Atelier de Revue (Nodemented Amigos)</h3>
            <p>Collaborez pour valider, enrichir et optimiser votre graphe</p>
          </div>
        </div>

        <div className="review-controls">
          <div className="review-mode-selector">
            <label>Mode de revue :</label>
            <div className="mode-options">
              <button 
                className={`mode-btn ${reviewMode === 'solo' ? 'active' : ''}`}
                onClick={() => setReviewMode('solo')}
              >
                <span className="mode-icon">👤</span>
                <span>{t('nodementedFunnel.review.modes.solo')}</span>
              </button>
              <button 
                className={`mode-btn ${reviewMode === 'team' ? 'active' : ''}`}
                onClick={() => setReviewMode('team')}
              >
                <span className="mode-icon">👥</span>
                <span>{t('nodementedFunnel.review.modes.team')}</span>
              </button>
            </div>
          </div>

          {reviewMode === 'team' && (
            <div className="collaboration-info">
              <div className="collab-badge">
                <span className="collab-icon">🔗</span>
                <span>{t('nodementedFunnel.review.collaboration.active')}</span>
              </div>
              <p className="collab-description">
                {t('nodementedFunnel.review.collaboration.description')}
              </p>
            </div>
          )}
        </div>

        <div className={`review-interface ${reviewMode === 'solo' ? 'review-solo' : 'review-team'}`}> 
          <div className={`review-panel ${reviewMode === 'solo' ? 'full-width' : ''}`}>
            <div className="panel-header">
              <h4>{t('nodementedFunnel.review.pendingNodes.title')}</h4>
              <div className="pending-stats">
                <span className="stat-badge">
                  {pendingNodes?.length || 0} {t('nodementedFunnel.review.pendingNodes.nodes')}
                </span>
              </div>
            </div>
            <PendingNodesPanel
              pendingNodes={pendingNodes}
              handleValidateNode={handleValidateNode}
              handleEditNode={handleEditNode}
              handleDeleteNode={handleDeleteNode}
              onUpdatePendingNode={savePendingNodeEdit}
              reviewMode={reviewMode}
            />
          </div>
          {reviewMode === 'team' && (
            <div className="comments-panel">
              <SessionCommentPanel />
            </div>
          )}
        </div>

        <div className="step-actions">
          <Button 
            onClick={() => goToStep('ingestion')}
            className="back-step-btn secondary"
          >
            <span className="step-arrow">←</span>
            <span>Retour à l'ingestion</span>
          </Button>
          <Button 
            onClick={() => { 
              completeStep('review', {
                reviewMode,
                validatedNodes: pendingNodes?.filter(n => n.validated)?.length || 0
              }); 
              goToStep('validation'); 
            }}
            className="next-step-btn"
          >
            <span>Étape suivante : Validation</span>
            <span className="step-arrow">→</span>
          </Button>
        </div>
      </div>
    );
  } else if (currentStep === 'validation') {
    stepContent = (
      <div className="funnel-step-content validation-step">
        <div className="step-header">
          <div className="step-icon">✅</div>
          <div className="step-info">
            <h3>Validation & Commit Local</h3>
            <p>Finalisez votre graphe et sauvegardez dans la base de connaissances</p>
          </div>
        </div>

        <div className="validation-summary">
          <div className="summary-cards">
            <div className="summary-card">
              <div className="card-icon">📦</div>
              <div className="card-content">
                <div className="card-number">{pendingNodes?.length || 0}</div>
                <div className="card-label">Nœuds à valider</div>
              </div>
            </div>
            <div className="summary-card">
              <div className="card-icon">🔗</div>
              <div className="card-content">
                <div className="card-number">
                  {pendingNodes?.reduce((acc, node) => acc + (node.relationships?.length || 0), 0) || 0}
                </div>
                <div className="card-label">Relations détectées</div>
              </div>
            </div>
            <div className="summary-card">
              <div className="card-icon">🧪</div>
              <div className="card-content">
                <div className="card-number">
                  {pendingNodes?.reduce((acc, node) => acc + (node.tests?.length || 0), 0) || 0}
                </div>
                <div className="card-label">Tests suggérés</div>
              </div>
            </div>
          </div>
        </div>

        <div className="validation-options">
          <div className="option-group">
            <h4>Options de sauvegarde :</h4>
            <div className="checkbox-group">
              <label className="checkbox-item">
                <input type="checkbox" defaultChecked />
                <span className="checkbox-custom"></span>
                <span>Sauvegarder dans Neo4j (tenant: {user?.tenant || 'default'})</span>
              </label>
              <label className="checkbox-item">
                <input type="checkbox" defaultChecked />
                <span className="checkbox-custom"></span>
                <span>Générer les tests automatiquement</span>
              </label>
              <label className="checkbox-item">
                <input type="checkbox" />
                <span className="checkbox-custom"></span>
                <span>Notifier l'équipe du commit</span>
              </label>
            </div>
          </div>
        </div>

        <div className="commit-preview">
          <h4>{t('nodementedFunnel.validation.commitPreview')}:</h4>
          <div className="commit-details">
            <div className="commit-message">
              <label>{t('nodementedFunnel.validation.commitMessage')}:</label>
              <textarea 
                placeholder={t('nodementedFunnel.validation.commitPlaceholder')}
                className="commit-input"
                rows={3}
                defaultValue={`${t('nodementedFunnel.validation.defaultMessage', { count: pendingNodes?.length || 0 })}\n\n${t('nodementedFunnel.validation.createdBy')}: ${user?.name || t('nodementedFunnel.validation.user')}\n${t('nodementedFunnel.validation.mode')}: ${stepData.review?.reviewMode || 'solo'}`}
              />
            </div>
          </div>
        </div>

        <div className="final-validation">
          <PendingNodesPanel
            pendingNodes={pendingNodes}
            handleValidateNode={handleValidateNode}
            handleEditNode={handleEditNode}
            handleDeleteNode={handleDeleteNode}
            onUpdatePendingNode={savePendingNodeEdit}
            readonly={true}
            showValidationStatus={true}
          />
        </div>

        <div className="step-actions">
          <Button 
            onClick={() => goToStep('review')}
            className="back-step-btn secondary"
          >
            <span className="step-arrow">←</span>
            <span>Retour à la revue</span>
          </Button>
          <Button 
            onClick={async () => {
              setLoading(true);
              try {
                // Sauvegarder tous les nœuds validés dans Neo4j
                const validatedNodes = pendingNodes?.filter(node => node.validated !== false) || [];
                
                for (const node of validatedNodes) {
                  await handleValidateNode(node);
                }
                
                completeStep('validation', {
                  savedNodes: validatedNodes.length,
                  timestamp: new Date().toISOString()
                });
                goToStep('export');
              } catch (error) {
                console.error(t('nodementedFunnel.validation.errors.console'), error);
                alert(t('nodementedFunnel.validation.errors.save') + error.message);
              } finally {
                setLoading(false);
              }
            }}
            className="commit-btn primary"
            disabled={loading}
          >
            {loading ? (
              <>
                <Spinner size="small" />
                <span>Commit en cours...</span>
              </>
            ) : (
              <>
                <span>💾 Valider & Sauvegarder</span>
                <span className="step-arrow">→</span>
              </>
            )}
          </Button>
        </div>
      </div>
    );
  } else if (currentStep === 'export') {
    stepContent = (
      <div className="funnel-step-content export-step">
        <div className="step-header">
          <div className="step-icon">📊</div>
          <div className="step-info">
            <h3>Surveillance & Export</h3>
            <p>Exportez votre graphe et configurez la surveillance continue</p>
          </div>
        </div>

        <div className="export-summary">
          <div className="success-indicator">
            <div className="success-icon">🎉</div>
            <div className="success-message">
              <h4>{t('nodementedFunnel.export.success.title')}</h4>
              <p>
                {stepData.validation?.savedNodes || 0} {t('nodementedFunnel.export.success.nodesSaved')}
              </p>
            </div>
          </div>
        </div>

        <div className="export-options">
          <h4>{t('nodementedFunnel.export.formats.title')}:</h4>
          <div className="export-formats">
            <div className="format-card">
              <div className="format-header">
                <span className="format-icon">📄</span>
                <h5>{t('nodementedFunnel.export.formats.json.title')}</h5>
              </div>
              <p>{t('nodementedFunnel.export.formats.json.description')}</p>
              <Button 
                onClick={() => exportToJSON()}
                className="export-btn secondary"
              >
                <span>💾</span>
                <span>Télécharger JSON</span>
              </Button>
            </div>

            <div className="format-card">
              <div className="format-header">
                <span className="format-icon">📊</span>
                <h5>Export CSV</h5>
              </div>
              <p>Données tabulaires pour analyse et reporting</p>
              <Button 
                onClick={() => exportToCSV()}
                className="export-btn secondary"
              >
                <span>📈</span>
                <span>Télécharger CSV</span>
              </Button>
            </div>

            <div className="format-card">
              <div className="format-header">
                <span className="format-icon">🎫</span>
                <h5>Export Jira</h5>
              </div>
              <p>Tickets de test et critères d'acceptation</p>
              <Button 
                onClick={() => exportToJira()}
                className="export-btn secondary"
              >
                <span>🔗</span>
                <span>Créer tickets Jira</span>
              </Button>
            </div>
          </div>
        </div>

        <div className="surveillance-config">
          <h4>Configuration de la surveillance :</h4>
          <div className="config-options">
            <label className="checkbox-item">
              <input type="checkbox" />
              <span className="checkbox-custom"></span>
              <span>Surveillance automatique des changements</span>
            </label>
            <label className="checkbox-item">
              <input type="checkbox" />
              <span className="checkbox-custom"></span>
              <span>Notifications hebdomadaires de couverture</span>
            </label>
            <label className="checkbox-item">
              <input type="checkbox" />
              <span className="checkbox-custom"></span>
              <span>Analyse d'impact sur modifications futures</span>
            </label>
          </div>
        </div>

        <div className="workflow-analytics">
          <h4>{t('nodementedFunnel.analytics')}</h4>
          <div className="analytics-grid">
            <TestCoverageTable nodes={nodes} />
            <GlobalTestsGraph nodes={nodes} />
          </div>
        </div>

        <div className="step-actions">
          <Button 
            onClick={() => goToStep('validation')}
            className="back-step-btn secondary"
          >
            <span className="step-arrow">←</span>
            <span>Retour validation</span>
          </Button>
          <Button 
            onClick={() => {
              completeStep('export', {
                exportFormats,
                completedAt: new Date().toISOString()
              });
              onFunnelComplete?.({
                totalSteps: 4,
                completedSteps: Object.keys(stepCompleted).length + 1,
                data: stepData,
                summary: {
                  nodesCreated: stepData.validation?.savedNodes || 0,
                  reviewMode: stepData.review?.reviewMode || 'solo',
                  exportFormats
                }
              });
            }}
            className="complete-btn primary"
          >
            <span>{t('nodementedFunnel.finishWorkflow')}</span>
          </Button>
        </div>
      </div>
    );
  }

  // Fonctions d'export
  const exportToJSON = () => {
    const graphData = {
      nodes: nodes,
      metadata: {
        exportedAt: new Date().toISOString(),
        exportedBy: user?.name,
        funnelData: stepData
      }
    };
    
    const blob = new Blob([JSON.stringify(graphData, null, 2)], {
      type: 'application/json'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `nodemented-graph-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
    
    setExportFormats(prev => [...prev, 'JSON']);
  };

  const exportToCSV = () => {
    const csvHeaders = ['Name', 'Type', 'Description', 'Technology', 'Status'];
    const csvRows = nodes.map(node => [
      node.name,
      node.type,
      node.description,
      node.technology,
      node.status
    ]);
    
    const csvContent = [csvHeaders, ...csvRows]
      .map(row => row.map(field => `"${field || ''}"`).join(','))
      .join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `nodemented-nodes-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    
    setExportFormats(prev => [...prev, 'CSV']);
  };

  const exportToJira = () => {
    // Simulation d'export Jira - ici vous intégreriez avec l'API Jira
    const jiraTickets = nodes.map(node => ({
      summary: `Test: ${node.name}`,
      description: `Critères d'acceptation pour ${node.name}\n\n${node.description}`,
      issueType: 'Test',
      labels: ['nodemented', 'automated-test']
    }));
    
    console.log('Tickets Jira générés:', jiraTickets);
    alert(`${jiraTickets.length} tickets Jira prêts à être créés`);
    
    setExportFormats(prev => [...prev, 'Jira']);
  };

  return (
    <div className="nodemented-funnel">
      <div className="funnel-header">
        <div className="funnel-title">
          <h2>{t('nodementedFunnel.title')}</h2>
          <p>{t('nodementedFunnel.subtitle')}</p>
        </div>
        
        <div className="funnel-progress">
          {FUNNEL_STEPS.map((step, index) => {
            const status = getStepStatus(step.id);
            const isLast = index === FUNNEL_STEPS.length - 1;
            
            return (
              <React.Fragment key={step.id}>
                <div 
                  className={`progress-step ${status}`}
                  onClick={() => status !== 'locked' && goToStep(step.id)}
                  style={{ '--step-color': step.color }}
                >
                  <div className="step-indicator">
                    <div className="step-icon-wrapper">
                      {status === 'completed' && <FaCheckCircle className="status-icon completed" />}
                      {status === 'active' && <FaPlay className="status-icon active" />}
                      {status === 'accessible' && <FaClock className="status-icon accessible" />}
                      {status === 'locked' && <FaLock className="status-icon locked" />}
                    </div>
                    <div className="step-number">{index + 1}</div>
                  </div>
                  <div className="step-details">
                    <div className="step-title">{step.label}</div>
                    <div className="step-description">{step.description}</div>
                  </div>
                </div>
                {!isLast && (
                  <div className={`step-connector ${status === 'completed' ? 'completed' : ''}`}>
                    <div className="connector-line"></div>
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
      
      <div className="funnel-content">
        {stepContent}
      </div>

      {/* 🔌 NOUVEAU - Modals d'intégration JIRA/GitHub/GitLab */}
      {showIntegrationSetup && (
        <IntegrationSetupModal
          provider={selectedProvider}
          isOpen={showIntegrationSetup}
          onClose={() => {
            setShowIntegrationSetup(false);
            setSelectedProvider(null);
          }}
          onSuccess={() => {
            refreshIntegrations();
            setShowIntegrationSetup(false);
            setSelectedProvider(null);
            // Toast notification (si vous avez react-toastify)
            // toast.success('Intégration configurée avec succès !');
          }}
        />
      )}

      {showJiraAnalyzer && (
        <JiraAnalyzerModal
          isOpen={showJiraAnalyzer}
          onClose={() => setShowJiraAnalyzer(false)}
          onNodesGenerated={({ nodes, tests, relations }) => {
            // Ajouter les nodes générés aux pendingNodes
            nodes.forEach(node => addPendingNode(node));
            
            // Toast notification
            console.log(`✅ ${nodes.length} nodes et ${tests.length} tests ajoutés depuis JIRA`);
            
            // Fermer la modal
            setShowJiraAnalyzer(false);
            
            // Optionnel: auto-expand la section des pending nodes
            // setExpandedMethod(null);
          }}
        />
      )}
    </div>
  );
}

export default NodementedFunnel;
